import { apiService } from './apiService';
import { API_CONFIG } from '@/config/apiConfig';
import { DeliveryAddress } from '@/types';
import { EntregaStatusPayload, PreparoStatusPayload } from './orderStatusService';

export interface DeliveryBoardItem {
  produtoID: number;
  nome: string;
  quantidade: number;
  valor: number;
}

export interface DeliveryBoardCliente {
  nome?: string;
  documento?: string;
  telefone?: string;
  email?: string;
}

export interface DeliveryBoardOrder {
  vendaID: number;
  identificadorVendaPDV?: string;
  numeroComanda?: string;
  dataCriacao: string;
  total: number;
  observacao?: string;
  cliente?: DeliveryBoardCliente;
  entregaStatus: EntregaStatusPayload;
  preparoStatus?: PreparoStatusPayload;
  itens: DeliveryBoardItem[];
}

export interface DeliveryBoardResponse {
  success: boolean;
  pedidos: DeliveryBoardOrder[];
  message?: string;
}

const normalizeDate = (value?: string): string => {
  if (!value) return '';
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? value : parsed.toISOString();
};

const normalizeAddress = (address?: any): DeliveryAddress | undefined => {
  if (!address) return undefined;
  return {
    street: address.street ?? address.Street ?? address.logradouro ?? address.Logradouro,
    logradouro: address.logradouro ?? address.Logradouro ?? address.street ?? address.Street,
    number: address.number ?? address.Number ?? address.numero ?? address.Numero,
    numero: address.numero ?? address.Numero ?? address.number ?? address.Number,
    complement: address.complement ?? address.Complement ?? address.complemento ?? address.Complemento,
    complemento: address.complemento ?? address.Complemento ?? address.complement ?? address.Complement,
    neighborhood: address.neighborhood ?? address.Neighborhood ?? address.bairro ?? address.Bairro,
    bairro: address.bairro ?? address.Bairro ?? address.neighborhood ?? address.Neighborhood,
    city: address.city ?? address.City ?? address.municipio ?? address.Municipio,
    municipio: address.municipio ?? address.Municipio ?? address.city ?? address.City,
    state: address.state ?? address.State ?? address.uf ?? address.UF,
    uf: address.uf ?? address.UF ?? address.state ?? address.State,
    zipCode: address.zipCode ?? address.ZipCode ?? address.cep ?? address.CEP,
    CEP: address.CEP ?? address.cep ?? address.zipCode ?? address.ZipCode,
    reference: address.reference ?? address.Reference ?? address.referencia ?? address.Referencia,
    referencia: address.referencia ?? address.Referencia ?? address.reference ?? address.Reference,
  };
};

const normalizeEntregaStatus = (status?: any): EntregaStatusPayload => {
  const eventosFonte = status?.eventos ?? status?.Eventos;
  const eventos = Array.isArray(eventosFonte)
    ? eventosFonte.map((evento: any) => ({
        data: evento.data ?? evento.Data,
        tipo: evento.tipo ?? evento.Tipo,
        comentario: evento.comentario ?? evento.Comentario,
      }))
    : undefined;

  return {
    status: status?.status ?? status?.Status ?? 'Pendente',
    motorista: status?.motorista ?? status?.Motorista,
    janela: status?.janela ?? status?.Janela,
    tipoPedido: status?.tipoPedido ?? status?.TipoPedido,
    endereco: normalizeAddress(status?.endereco ?? status?.Endereco),
    taxaEntrega: status?.taxaEntrega ?? status?.TaxaEntrega,
    observacao: status?.observacao ?? status?.Observacao,
    eventos,
  };
};

const normalizePreparoStatus = (status?: any): PreparoStatusPayload | undefined => {
  if (!status) return undefined;
  return {
    status: status.status ?? status.Status,
    estacao: status.estacao ?? status.Estacao,
  };
};

const normalizeCliente = (cliente?: any): DeliveryBoardCliente | undefined => {
  if (!cliente) return undefined;
  return {
    nome: cliente.nome ?? cliente.Nome,
    documento: cliente.documento ?? cliente.Documento,
    telefone: cliente.telefone ?? cliente.Telefone,
    email: cliente.email ?? cliente.Email,
  };
};

const normalizeItens = (itens?: any[]): DeliveryBoardItem[] => {
  if (!Array.isArray(itens)) return [];
  return itens.map((item) => ({
    produtoID: item.produtoID ?? item.ProdutoID ?? 0,
    nome: item.nome ?? item.Nome ?? '',
    quantidade: Number(item.quantidade ?? item.Quantidade ?? 0),
    valor: Number(item.valor ?? item.Valor ?? 0),
  }));
};

const normalizeOrder = (order: any): DeliveryBoardOrder => ({
  vendaID: order.vendaID ?? order.VendaID ?? 0,
  identificadorVendaPDV: order.identificadorVendaPDV ?? order.IdentificadorVendaPDV,
  numeroComanda: order.numeroComanda ?? order.NumeroComanda,
  dataCriacao: normalizeDate(order.dataCriacao ?? order.DataCriacao),
  total: Number(order.total ?? order.Total ?? 0),
  observacao: order.observacao ?? order.Observacao,
  cliente: normalizeCliente(order.cliente ?? order.Cliente),
  entregaStatus: normalizeEntregaStatus(order.entregaStatus ?? order.EntregaStatus),
  preparoStatus: normalizePreparoStatus(order.preparoStatus ?? order.PreparoStatus),
  itens: normalizeItens(order.itens ?? order.Itens),
});

export const deliveryBoardService = {
  async list(): Promise<DeliveryBoardResponse> {
    const endpoint = API_CONFIG.endpoints.orders.deliveryBoard;
    const response = await apiService.get(endpoint);

    if (response?.success && Array.isArray(response.pedidos)) {
      return {
        success: true,
        pedidos: response.pedidos.map(normalizeOrder),
      };
    }

    return {
      success: false,
      pedidos: [],
      message: response?.message ?? 'N�o foi poss�vel carregar as entregas.',
    };
  },
};
